import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import {
    isValidName,
    isValidPhoneNumber,
    isValidEmail,
    isValidPostalCode,
} from "../../../helpers/input-validations";
import CustomInput from "../../common/CustomInput";
import Button from "../../common/Button";
import { StateOptions, CountryOptions, TimezoneOptions } from '../../../helpers/options';
import { updateReelMember } from "../../../redux/modules/user/actions";

const ContactInfoEdit = ({
    onClose,
    user,
}) => {
    const dispatch = useDispatch();
    const [primaryEmail, setPrimaryEmail] = useState(user?.primaryEmail ?? "");
    const [primaryPhone, setPrimaryPhone] = useState(user?.primaryPhone ?? "");
    const [primaryPhoneCountryCode, setPrimaryPhoneCountryCode] = useState(user?.primaryPhoneCountryCode ?? "");
    const [alternatePhone, setAlternatePhone] = useState(user?.alternatePhone ?? "");
    const [alternatePhoneCountryCode, setAlternatePhoneCountryCode] = useState(user?.alternatePhoneCountryCode ?? "");
    const [address, setAddress] = useState(user?.address ?? "");
    const [unit, setUnit] = useState(user?.unit ?? "");
    const [poBox, setPoBox] = useState(user?.poBox ?? "");
    const [city, setCity] = useState(user?.city ?? "");
    const [province, setProvince] = useState(user?.province ?? "");
    const [postalCode, setPostalCode] = useState(user?.postalCode ?? "");
    const [country, setCountry] = useState(user?.country ?? "");
    const [timezone, setTimezone] = useState(user?.timezone ?? "");
    const [contactInfoComments, setContactInfoComments] = useState(user?.contactInfoComments ?? "");

    const [clicked, setClicked] = useState(false);

    const [primaryEmailError, setPrimaryEmailError] = useState(false);
    const [primaryPhoneError, setPrimaryPhoneError] = useState(false);
    const [addressError, setAddressError] = useState(false);
    const [provinceError, setProvinceError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [postalCodeError, setPostalCodeError] = useState(false);
    const [timezoneError, setTimezoneError] = useState(false);
    const [countryError, setCountryError] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    const fieldsToCheck = [
        {
            field: primaryEmail,
            checkFn: isValidEmail,
            setErrorFn: setPrimaryEmailError,
            name: 'primaryEmail',
        },
        {
            field: primaryPhone,
            checkFn: isValidPhoneNumber,
            setErrorFn: setPrimaryPhoneError,
            name: 'primaryPhone',
        },
        {
            field: address,
            checkFn: isValidName,
            setErrorFn: setAddressError,
            name: 'address',
        },
        {
            field: city,
            checkFn: isValidName,
            setErrorFn: setCityError,
            name: 'city',
        },
        {
            field: province,
            checkFn: isValidName,
            setErrorFn: setProvinceError,
            name: 'province',
        },
        {
            field: postalCode,
            checkFn: isValidPostalCode,
            setErrorFn: setPostalCodeError,
            name: "postalCode",
        },
        {
            field: country,
            checkFn: isValidName,
            setErrorFn: setCountryError,
            name: "country",
        },
        {
            field: timezone,
            checkFn: isValidName,
            setErrorFn: setTimezoneError,
            name: "timezone",
        }
    ];

    useEffect(() => {
        if (clicked) {
            fieldsToCheck.forEach(({ field, checkFn, setErrorFn }) => {
                const hasError = !checkFn(field);
                setErrorFn(hasError);
            });
        }
    }, [clicked, ...fieldsToCheck.map(({ field }) => field)]);

    const manageStep = async () => {
        setClicked(true);
        let allFieldsValid = true;
        let fieldsToValidate = fieldsToCheck;

        fieldsToValidate.forEach(({ field, checkFn, setErrorFn }) => {
            const hasError = !checkFn(field);
            setErrorFn(hasError);

            if (hasError) {
                allFieldsValid = false;
            }
        });

        if (allFieldsValid) {
            try {
                setIsLoading(true);
                const primaryCountryCode = primaryPhoneCountryCode && primaryPhoneCountryCode.length == 0 ? "+1" : primaryPhoneCountryCode;
                const alternateCountryCode = alternatePhoneCountryCode && alternatePhoneCountryCode.length == 0 ? "+1" : alternatePhoneCountryCode;

                const updatedUserData = {
                    primaryEmail,
                    primaryPhone,
                    primaryPhoneCountryCode: primaryCountryCode,
                    alternatePhone,
                    alternatePhoneCountryCode: alternateCountryCode,
                    address,
                    unit,
                    poBox,
                    city,
                    province,
                    postalCode,
                    country,
                    timezone,
                    contactInfoComments
                };
                await dispatch(updateReelMember(user.userId, {
                    ...user,
                    ...updatedUserData
                }));
                onClose();
            } catch (error) {
                console.error('Error updating user:', error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (<>
        <div className="flex flex-col h-[75vh]">
            <div className="flex-1 overflow-y-auto" id="scrollable-content">
                <div className="grid grid-cols-12 gap-3 mt-2">
                    <CustomInput
                        name="primaryEmail"
                        className="col-span-12"
                        label="Email Address"
                        placeholder="Enter Email Address"
                        type="text"
                        value={primaryEmail}
                        onChange={(e) => setPrimaryEmail(e.target.value)}
                        error={primaryEmailError}
                        required
                    />

                    <CustomInput
                        name="primaryPhone"
                        className="col-span-6"
                        label="Primary Phone Number"
                        value={primaryPhone}
                        selectValue={primaryPhoneCountryCode}
                        placeholder="(555) 000-0000"
                        type="phone"
                        required
                        handleSelect={(e) => setPrimaryPhoneCountryCode(e.target.value)}
                        onChange={(e) => setPrimaryPhone(e.target.value)}
                        error={primaryPhoneError}
                    />

                    <CustomInput
                        name="alternatePhone"
                        className="col-span-6"
                        label="Alternate Phone Number"
                        value={alternatePhone}
                        selectValue={alternatePhoneCountryCode}
                        placeholder="(555) 000-0000"
                        type="phone"
                        handleSelect={(e) => setAlternatePhoneCountryCode(e.target.value)}
                        onChange={(e) => setAlternatePhone(e.target.value)}
                    />

                    <p className="col-span-12 font-medium text-gray-900 text-body-md">
                        Address
                    </p>

                    <CustomInput
                        name="address"
                        className="col-span-12"
                        value={address}
                        label="Address"
                        placeholder=" Enter Street Number & Street Name"
                        type="text"
                        required
                        onChange={(e) => setAddress(e.target.value)}
                        error={addressError}
                    />

                    <CustomInput
                        name="unit"
                        className="col-span-6"
                        value={unit}
                        label="Unit Number"
                        placeholder="Enter Unit Number"
                        type="text"
                        onChange={(e) => setUnit(e.target.value)}
                    />
                    <CustomInput
                        name="poBox"
                        className="col-span-6"
                        label="PO Box"
                        value={poBox}
                        placeholder="PO Box"
                        type="age"
                        onChange={(e) => setPoBox(e.target.value)}
                    />

                    <CustomInput
                        name="city"
                        className="col-span-6"
                        label="City"
                        value={city}
                        type="text"
                        required
                        onChange={(e) => setCity(e.target.value)}
                        error={cityError}
                    />
                    <CustomInput
                        options={StateOptions}
                        name="province"
                        value={province}
                        className="col-span-6"
                        label="Province"
                        type="dropdown"
                        onChange={(e) => setProvince(e.value)}
                        required
                        error={provinceError}
                    />
                    <CustomInput
                        name="postalCode"
                        className="col-span-6"
                        label="Postal Code"
                        value={postalCode}
                        placeholder="Enter Postal Code"
                        type="postalCode"
                        onChange={(e) => setPostalCode(e.target.value)}
                        error={postalCodeError}
                        required
                    />
                    <CustomInput
                        options={CountryOptions}
                        name="countryOptions"
                        className="col-span-6"
                        value={country}
                        label="Country"
                        type="dropdown"
                        required
                        onChange={(e) => setCountry(e.value)}
                        error={countryError}
                    />

                    <CustomInput
                        options={TimezoneOptions}
                        name="timezone"
                        className="col-span-12"
                        value={timezone}
                        label="Time Zone"
                        type="dropdown"
                        onChange={(e) => setTimezone(e.value)}
                        onFocus={() => {
                            setTimeout(() => {
                                const el = document.getElementById('scrollable-content');
                                if (el) {
                                    el.scrollTop = el.scrollHeight;
                                }
                            }, 0);
                        }}
                        required
                        error={timezoneError}
                    />

                    <CustomInput
                        name="contactInfoComments"
                        className="col-span-12"
                        label="Comments"
                        placeholder="Enter Comments"
                        type="text"
                        value={contactInfoComments}
                        onChange={(e) => setContactInfoComments(e.target.value)}
                    />

                </div>
            </div>
            <div className="flex items-center justify-center py-3 mt-auto ">
                <div className="flex items-center justify-center py-3">
                    <div className="flex items-center justify-center space-x-5">
                        <Button
                            className="min-w-[200px]"
                            text="Cancel"
                            type="secondary"
                            onClick={() =>
                                onClose(false)
                            }
                        />
                        <Button
                            className="min-w-[200px]"
                            text={isLoading ? "Updating..." : "Update & Save"}
                            type="primary"
                            onClick={() => manageStep()}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            </div>
        </div>
    </>);
};

export default ContactInfoEdit;
